import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/layout/PageHeader';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import Can from '../../auth/Can';
import { usePermission } from '../../auth/usePermission';
import CategoryBadge from '../../components/candidates/CategoryBadge';
import {
  listWorkflowCategories,
  listStepDefinitions,
  createStepGroup,
  updateStepGroup,
  deleteStepGroup,
  deleteWorkflowStep,
  reorderWorkflowSteps,
  reorderStepGroups,
  updateWorkflowCategory,
} from '../../api/workflow';
import WorkflowStepEditModal from './WorkflowStepEditModal';
import AddWorkflowStepModal from './AddWorkflowStepModal';
import CopyWorkflowStepModal from './CopyWorkflowStepModal';
import BulkCopyWorkflowStepsModal from './BulkCopyWorkflowStepsModal';
import CopyStepGroupModal from './CopyStepGroupModal';
import { pushToast } from '../../components/ui/toastBus';

const FIELD_TYPE_LABEL = {
  text: 'text',
  number: 'number',
  checkbox: 'check box',
  radio: 'radio button',
  select: 'select field',
  multiselect: 'multi select field',
  date: 'date',
};

// The canonical processing order: registration first (handled separately), then each group's
// steps in the group's own order, then ungrouped steps. This is also exactly what gets sent
// to the reorder endpoint, so what you see is what gets saved.
function canonicalOrder(category) {
  if (!category) return [];
  const groups = [...(category.groups || [])].sort((a, b) => a.sort_order - b.sort_order);
  const rest = category.steps.filter((s) => s.code !== 'registration');
  const inGroup = (id) => rest.filter((s) => (s.step_group_id ?? null) === id).sort((a, b) => a.sort_order - b.sort_order);

  const ordered = [];
  groups.forEach((g) => ordered.push(...inGroup(g.id)));
  ordered.push(...inGroup(null));
  return ordered;
}

function GroupHeader({ group, onRename, onCopy, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(group.name);

  const save = () => {
    setEditing(false);
    if (value.trim() && value.trim() !== group.name) onRename(value.trim());
    else setValue(group.name);
  };

  return (
    <div className="mb-2 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Can perm="workflow.manage">
          <span className="select-none text-gray-300" title="Drag to reorder group">
            ⠿
          </span>
        </Can>
        {editing ? (
          <input
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onBlur={save}
            onKeyDown={(e) => {
              if (e.key === 'Enter') save();
              if (e.key === 'Escape') {
                setValue(group.name);
                setEditing(false);
              }
            }}
            className="rounded border border-primary px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-gray-700"
          />
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-xs font-semibold uppercase tracking-wide text-gray-500 hover:text-primary"
            title="Click to rename"
          >
            {group.name}
          </button>
        )}
      </div>
      <Can perm="workflow.manage">
        <div className="flex items-center gap-3">
          <button onClick={onCopy} className="text-xs text-primary hover:underline">
            Copy
          </button>
          <button onClick={onDelete} className="text-xs text-gray-400 hover:text-red-600">
            Remove group
          </button>
        </div>
      </Can>
    </div>
  );
}

function SlaEditor({ category, onSave, saving }) {
  const { can } = usePermission();
  const canManage = can('workflow.manage');
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(category.sla_days ?? '');

  useEffect(() => {
    setValue(category.sla_days ?? '');
  }, [category.id, category.sla_days]);

  const cancel = () => {
    setValue(category.sla_days ?? '');
    setEditing(false);
  };

  const update = () => {
    const trimmed = String(value).trim();
    if (trimmed === '') return;
    const next = Number(trimmed);
    if (!Number.isInteger(next) || next < 1) return;
    setEditing(false);
    if (next === (category.sla_days ?? null)) return;
    onSave(next);
  };

  const remove = () => {
    setEditing(false);
    setValue('');
    if (category.sla_days != null) onSave(null);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1.5">
        <span className="text-xs text-gray-500">SLA</span>
        <input
          autoFocus
          type="number"
          min={1}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') update();
            if (e.key === 'Escape') cancel();
          }}
          className="w-16 rounded border border-primary px-1.5 py-0.5 text-xs text-gray-700"
        />
        <span className="text-xs text-gray-500">days</span>
        <button
          type="button"
          onClick={update}
          disabled={saving || String(value).trim() === ''}
          className="rounded bg-primary px-2 py-0.5 text-xs font-medium text-white hover:bg-primary-dark disabled:opacity-50"
        >
          Update
        </button>
        <button type="button" onClick={cancel} className="text-xs text-gray-400 hover:underline">
          Cancel
        </button>
      </div>
    );
  }

  if (!canManage) {
    return (
      <span className={`text-xs ${category.sla_days ? 'font-medium text-red-600' : 'text-gray-500'}`}>
        {category.sla_days ? `SLA: ${category.sla_days} days` : 'No SLA set'}
      </span>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        onClick={() => setEditing(true)}
        disabled={saving}
        className={`text-xs hover:text-primary ${category.sla_days ? 'font-medium text-red-600' : 'text-gray-500'}`}
        title="Click to set the SLA for this workflow"
      >
        {category.sla_days ? `SLA: ${category.sla_days} days` : 'Set SLA…'}
      </button>
      {category.sla_days != null && (
        <button
          type="button"
          onClick={remove}
          disabled={saving}
          className="text-xs text-gray-400 hover:text-red-600"
          title="Remove this workflow's SLA"
        >
          ✕
        </button>
      )}
    </div>
  );
}

function StepRow({ step, displayNumber, draggable, isDragging, onDragStart, onDragEnd, onDrop, onEdit, onCopy, onDelete, pinned, selected, onToggleSelect }) {
  return (
    <li
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={(e) => draggable && e.preventDefault()}
      onDrop={onDrop}
      className={`flex items-start justify-between gap-4 rounded-md border border-transparent px-2 py-2 ${
        isDragging ? 'opacity-40' : ''
      } ${draggable ? 'cursor-grab hover:border-gray-200 hover:bg-gray-50' : ''}`}
    >
      <div className="flex items-start gap-2">
        {onToggleSelect && (
          <Can perm="workflow.manage">
            <input
              type="checkbox"
              className="mt-1 accent-primary"
              checked={!!selected}
              onChange={onToggleSelect}
              onClick={(e) => e.stopPropagation()}
            />
          </Can>
        )}
        {draggable && <span className="mt-0.5 select-none text-gray-300">⠿</span>}
        <div>
          <p className="text-sm text-gray-800">
            {displayNumber}. {step.title}
            {pinned && <span className="ml-2 text-xs font-normal text-gray-400">(fixed)</span>}
            {step.sla_days && <span className="ml-2 text-xs font-medium text-red-600">· SLA {step.sla_days}d</span>}
            {step.skip_enabled && <span className="ml-2 text-xs font-normal text-gray-400">· Skippable</span>}
          </p>
          {step.required_documents?.length > 0 && (
            <p className="mt-0.5 text-xs text-gray-400">
              Documents:{' '}
              {step.required_documents
                .map((d) => {
                  const label = !d.required ? `${d.code} (optional)` : d.verify ? `${d.code} (verify)` : d.code;
                  return d.depends_on ? `${label} (conditional)` : label;
                })
                .join(', ')}
            </p>
          )}
          {step.fields?.length > 0 && (
            <p className="mt-0.5 text-xs text-gray-400">
              Fields: {step.fields.map((f) => `${f.label} (${FIELD_TYPE_LABEL[f.type]}${f.validation?.required ? ', required' : ''})`).join(', ')}
            </p>
          )}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Badge tone={step.kind === 'decision' ? 'amber' : 'gray'}>{step.kind === 'decision' ? 'Decision' : 'Task'}</Badge>
        <Can perm="workflow.manage">
          <button onClick={onEdit} className="text-xs text-primary hover:underline">
            Edit
          </button>
          {onCopy && (
            <button onClick={onCopy} className="text-xs text-primary hover:underline">
              Copy
            </button>
          )}
          {!pinned && (
            <button onClick={onDelete} className="text-xs text-red-500 hover:underline">
              Remove
            </button>
          )}
        </Can>
      </div>
    </li>
  );
}

export default function WorkflowReference() {
  const queryClient = useQueryClient();
  const categoriesQuery = useQuery({ queryKey: ['workflow-categories'], queryFn: listWorkflowCategories });
  const stepDefinitionsQuery = useQuery({ queryKey: ['step-definitions'], queryFn: listStepDefinitions });

  const [selectedId, setSelectedId] = useState(null);
  const [editingStepId, setEditingStepId] = useState(null);
  const [addStepOpen, setAddStepOpen] = useState(false);
  const [addingGroup, setAddingGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [deleteStepTarget, setDeleteStepTarget] = useState(null);
  const [copyStepTarget, setCopyStepTarget] = useState(null);
  const [deleteGroupTarget, setDeleteGroupTarget] = useState(null);
  const [copyGroupTarget, setCopyGroupTarget] = useState(null);
  const [draggedId, setDraggedId] = useState(null);
  const [localSteps, setLocalSteps] = useState(null);
  const [draggedGroupId, setDraggedGroupId] = useState(null);
  const [localGroupOrder, setLocalGroupOrder] = useState(null);
  const [selectedStepIds, setSelectedStepIds] = useState([]);
  const [bulkCopyOpen, setBulkCopyOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const selected = categoriesQuery.data?.find((c) => c.id === selectedId) || categoriesQuery.data?.[0];
  const editingStep = editingStepId ? selected?.steps.find((s) => s.id === editingStepId) : null;
  const registration = selected?.steps.find((s) => s.code === 'registration');
  const groupsBySortOrder = [...(selected?.groups || [])].sort((a, b) => a.sort_order - b.sort_order);
  const groups = localGroupOrder
    ? localGroupOrder.map((id) => groupsBySortOrder.find((g) => g.id === id)).filter(Boolean)
    : groupsBySortOrder;

  useEffect(() => {
    setLocalSteps(null);
    setSelectedStepIds([]);
    setLocalGroupOrder(null);
  }, [selected?.id]);

  const steps = localSteps ?? canonicalOrder(selected);
  const stepsIn = (groupId) => steps.filter((s) => (s.step_group_id ?? null) === groupId);

  // The number shown next to each step is its position in this visible list (registration is
  // always 1), not the raw `sort_order` from the database — removed steps keep their old
  // sort_order forever (removal only deactivates a step, it doesn't delete the row), which
  // otherwise left permanent gaps in the numbers shown here (e.g. "10." followed by "13.").
  const displayNumbers = new Map(steps.map((s, i) => [s.id, i + 2]));

  // Registration is fixed and never part of `steps` (canonicalOrder excludes it), so every
  // selectable step is already eligible for bulk copy/remove.
  const allStepsSelected = steps.length > 0 && steps.every((s) => selectedStepIds.includes(s.id));
  const toggleStepSelection = (id) =>
    setSelectedStepIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const selectAllSteps = () => setSelectedStepIds(steps.map((s) => s.id));
  const clearStepSelection = () => setSelectedStepIds([]);
  const selectedSteps = steps.filter((s) => selectedStepIds.includes(s.id));

  const availableDefinitions = (stepDefinitionsQuery.data || []).filter(
    (d) => !selected?.steps.some((s) => s.code === d.code)
  );

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['workflow-categories'] });

  const slaMutation = useMutation({
    mutationFn: ({ id, sla_days }) => updateWorkflowCategory(id, { sla_days }),
    onSuccess: () => {
      pushToast({ type: 'success', message: 'SLA updated.' });
      invalidate();
    },
    onError: (err) => pushToast({ type: 'error', message: err.response?.data?.message || 'Could not update SLA.' }),
  });

  const groupMutation = useMutation({
    mutationFn: () => createStepGroup(selected.id, newGroupName),
    onSuccess: () => {
      pushToast({ type: 'success', message: 'Group created.' });
      setNewGroupName('');
      setAddingGroup(false);
      invalidate();
    },
    onError: (err) => pushToast({ type: 'error', message: err.response?.data?.message || 'Could not create group.' }),
  });

  const renameGroupMutation = useMutation({
    mutationFn: ({ id, name }) => updateStepGroup(id, name),
    onSuccess: invalidate,
    onError: (err) => pushToast({ type: 'error', message: err.response?.data?.message || 'Could not rename group.' }),
  });

  const deleteGroupMutation = useMutation({
    mutationFn: (id) => deleteStepGroup(id),
    onSuccess: () => {
      pushToast({ type: 'success', message: 'Group deleted.' });
      setDeleteGroupTarget(null);
      invalidate();
    },
    onError: (err) => pushToast({ type: 'error', message: err.response?.data?.message || 'Could not delete group.' }),
  });

  const deleteStepMutation = useMutation({
    mutationFn: (id) => deleteWorkflowStep(id),
    onSuccess: () => {
      pushToast({ type: 'success', message: 'Step removed.' });
      setDeleteStepTarget(null);
      invalidate();
    },
    onError: (err) => pushToast({ type: 'error', message: err.response?.data?.message || 'Could not remove step.' }),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids) => Promise.all(ids.map((id) => deleteWorkflowStep(id))),
    onSuccess: (_, ids) => {
      pushToast({ type: 'success', message: `${ids.length} step${ids.length === 1 ? '' : 's'} removed.` });
      setBulkDeleteOpen(false);
      setSelectedStepIds([]);
      invalidate();
    },
    onError: (err) =>
      pushToast({ type: 'error', message: err.response?.data?.message || 'Could not remove the selected steps.' }),
  });

  const reorderMutation = useMutation({
    mutationFn: (nextSteps) =>
      reorderWorkflowSteps(selected.id, [
        { id: registration.id, step_group_id: null },
        ...nextSteps.map((s) => ({ id: s.id, step_group_id: s.step_group_id ?? null })),
      ]),
    onSuccess: invalidate,
    onError: (err) => {
      pushToast({ type: 'error', message: err.response?.data?.message || 'Could not reorder.' });
      setLocalSteps(null);
    },
  });

  const moveStep = (draggedStepId, targetStepId, targetGroupId) => {
    const dragged = steps.find((s) => s.id === draggedStepId);
    if (!dragged) return;
    const without = steps.filter((s) => s.id !== draggedStepId);

    let insertAt = without.length;
    if (targetStepId) {
      insertAt = without.findIndex((s) => s.id === targetStepId);
    } else {
      for (let i = without.length - 1; i >= 0; i--) {
        if ((without[i].step_group_id ?? null) === targetGroupId) {
          insertAt = i + 1;
          break;
        }
      }
    }

    const next = [...without];
    next.splice(insertAt, 0, { ...dragged, step_group_id: targetGroupId });
    // Registration holds sort_order 1, so these steps start at 2 — keep the displayed numbers
    // in sync with what the reorder endpoint is about to persist, instead of the stale values
    // from before the drag (which otherwise only appeared correct after a refetch/reload).
    const renumbered = next.map((s, i) => ({ ...s, sort_order: i + 2 }));
    setLocalSteps(renumbered);
    reorderMutation.mutate(renumbered);
  };

  const dropOnStep = (e, targetStep) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedId && draggedId !== targetStep.id) moveStep(draggedId, targetStep.id, targetStep.step_group_id ?? null);
    setDraggedId(null);
  };

  const dropOnSection = (e, groupId) => {
    e.preventDefault();
    if (draggedId) moveStep(draggedId, null, groupId);
    setDraggedId(null);
  };

  const reorderGroupsMutation = useMutation({
    mutationFn: (groupIds) => reorderStepGroups(selected.id, groupIds),
    onSuccess: invalidate,
    onError: (err) => {
      pushToast({ type: 'error', message: err.response?.data?.message || 'Could not reorder groups.' });
      setLocalGroupOrder(null);
    },
  });

  // Groups carry their steps with them by construction: a step's rendered position only ever
  // depends on its own group membership + sort_order, so moving a group's position here never
  // needs to touch any step.
  const moveGroup = (movedGroupId, targetGroupId) => {
    const order = groups.map((g) => g.id);
    const from = order.indexOf(movedGroupId);
    if (from === -1) return;
    order.splice(from, 1);
    const to = targetGroupId ? order.indexOf(targetGroupId) : order.length;
    order.splice(to === -1 ? order.length : to, 0, movedGroupId);
    setLocalGroupOrder(order);
    reorderGroupsMutation.mutate(order);
  };

  const dropOnGroup = (e, targetGroup) => {
    if (!draggedGroupId) return;
    e.preventDefault();
    e.stopPropagation();
    if (draggedGroupId !== targetGroup.id) moveGroup(draggedGroupId, targetGroup.id);
    setDraggedGroupId(null);
  };

  const dropAfterGroups = (e) => {
    if (!draggedGroupId) return;
    e.preventDefault();
    moveGroup(draggedGroupId, null);
    setDraggedGroupId(null);
  };

  const renderStep = (step) => (
    <StepRow
      key={step.id}
      step={step}
      displayNumber={displayNumbers.get(step.id)}
      draggable
      isDragging={draggedId === step.id}
      onDragStart={() => setDraggedId(step.id)}
      onDragEnd={() => setDraggedId(null)}
      onDrop={(e) => dropOnStep(e, step)}
      onEdit={() => setEditingStepId(step.id)}
      onCopy={() => setCopyStepTarget(step)}
      onDelete={() => setDeleteStepTarget(step)}
      selected={selectedStepIds.includes(step.id)}
      onToggleSelect={() => toggleStepSelection(step.id)}
    />
  );

  return (
    <div>
      <PageHeader title="Workflow" description="Reference for recruitment categories and their step sequences." />

      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {(categoriesQuery.data || []).map((category) => (
          <button
            key={category.id}
            onClick={() => setSelectedId(category.id)}
            className={`rounded-lg border-2 p-4 text-left transition-colors ${
              selected?.id === category.id ? 'border-primary bg-primary-50' : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <p className="text-sm font-semibold text-gray-900">{category.code}</p>
            <div className="mt-1">
              <CategoryBadge category={category} />
            </div>
            <p className="mt-2 text-xs text-gray-500">
              {category.steps.length} steps
              {category.sla_days && <span className="font-medium text-red-600"> · SLA {category.sla_days}d</span>}
            </p>
          </button>
        ))}
      </div>

      {selected && (
        <Card>
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h2 className="text-sm font-semibold text-gray-900">{selected.name}</h2>
              <SlaEditor
                category={selected}
                saving={slaMutation.isPending}
                onSave={(sla_days) => slaMutation.mutate({ id: selected.id, sla_days })}
              />
            </div>
            <Can perm="workflow.manage">
              <div className="flex gap-2">
                <Button variant="secondary" onClick={() => setAddingGroup(true)}>
                  + Group
                </Button>
                <Button onClick={() => setAddStepOpen(true)}>+ Step</Button>
              </div>
            </Can>
          </div>

          {addingGroup && (
            <div className="mb-4 flex items-end gap-2 rounded-md bg-gray-50 p-3">
              <Input
                label="Group name"
                autoFocus
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                className="flex-1"
              />
              <Button
                onClick={() => newGroupName.trim() && groupMutation.mutate()}
                loading={groupMutation.isPending}
                disabled={!newGroupName.trim()}
              >
                Add
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setAddingGroup(false);
                  setNewGroupName('');
                }}
              >
                Cancel
              </Button>
            </div>
          )}

          {registration && (
            <ol className="mb-2 border-b border-gray-100 pb-2">
              <StepRow
                step={registration}
                displayNumber={1}
                pinned
                onEdit={() => setEditingStepId(registration.id)}
                onCopy={() => setCopyStepTarget(registration)}
              />
            </ol>
          )}

          {steps.length > 0 && (
            <Can perm="workflow.manage">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-md bg-gray-50 px-3 py-2">
                <label className="flex items-center gap-2 text-xs text-gray-600">
                  <input
                    type="checkbox"
                    className="accent-primary"
                    checked={allStepsSelected}
                    onChange={(e) => (e.target.checked ? selectAllSteps() : clearStepSelection())}
                  />
                  Select all steps ({steps.length}) — Candidate Registration is fixed and can't be selected.
                </label>
                {selectedStepIds.length > 0 && (
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-500">{selectedStepIds.length} selected</span>
                    <button type="button" onClick={() => setBulkCopyOpen(true)} className="text-xs text-primary hover:underline">
                      Copy selected
                    </button>
                    <button type="button" onClick={() => setBulkDeleteOpen(true)} className="text-xs text-red-500 hover:underline">
                      Remove selected
                    </button>
                    <button type="button" onClick={clearStepSelection} className="text-xs text-gray-400 hover:underline">
                      Clear
                    </button>
                  </div>
                )}
              </div>
            </Can>
          )}

          <div onDragOver={(e) => draggedGroupId && e.preventDefault()} onDrop={dropAfterGroups}>
            {groups.map((group) => (
              <div
                key={group.id}
                draggable
                onDragStart={(e) => {
                  e.stopPropagation();
                  setDraggedGroupId(group.id);
                }}
                onDragEnd={() => setDraggedGroupId(null)}
                onDragOver={(e) => draggedGroupId && e.preventDefault()}
                onDrop={(e) => dropOnGroup(e, group)}
                className={`mb-4 cursor-grab rounded-lg border border-primary/20 bg-primary-50/50 p-3 ${
                  draggedGroupId === group.id ? 'opacity-40' : ''
                }`}
              >
                <GroupHeader
                  group={group}
                  onRename={(name) => renameGroupMutation.mutate({ id: group.id, name })}
                  onCopy={() => setCopyGroupTarget(group)}
                  onDelete={() => setDeleteGroupTarget(group)}
                />
                <ol
                  className="min-h-[2rem] space-y-1 rounded-md"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => dropOnSection(e, group.id)}
                >
                  {stepsIn(group.id).map(renderStep)}
                  {stepsIn(group.id).length === 0 && <p className="px-2 py-1 text-xs text-gray-300">Drag a step here.</p>}
                </ol>
              </div>
            ))}
          </div>

          <div>
            {groups.length > 0 && <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">Ungrouped</p>}
            <ol
              className="min-h-[2rem] space-y-1 rounded-md"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => dropOnSection(e, null)}
            >
              {stepsIn(null).map(renderStep)}
              {groups.length > 0 && stepsIn(null).length === 0 && (
                <p className="px-2 py-1 text-xs text-gray-300">Drag a step here.</p>
              )}
            </ol>
          </div>
        </Card>
      )}

      <WorkflowStepEditModal open={!!editingStepId} onClose={() => setEditingStepId(null)} workflowStep={editingStep} />
      <AddWorkflowStepModal
        open={addStepOpen}
        onClose={() => setAddStepOpen(false)}
        category={selected}
        availableDefinitions={availableDefinitions}
      />
      <CopyWorkflowStepModal
        open={!!copyStepTarget}
        onClose={() => setCopyStepTarget(null)}
        step={copyStepTarget}
        categories={categoriesQuery.data || []}
        sourceCategoryId={selected?.id}
      />
      <BulkCopyWorkflowStepsModal
        open={bulkCopyOpen}
        onClose={() => setBulkCopyOpen(false)}
        onCopied={() => {
          setBulkCopyOpen(false);
          setSelectedStepIds([]);
        }}
        steps={selectedSteps}
        categories={categoriesQuery.data || []}
        sourceCategoryId={selected?.id}
      />
      <CopyStepGroupModal
        open={!!copyGroupTarget}
        onClose={() => setCopyGroupTarget(null)}
        group={copyGroupTarget}
        categories={categoriesQuery.data || []}
        sourceCategoryId={selected?.id}
      />

      <ConfirmDialog
        open={!!deleteStepTarget}
        onClose={() => setDeleteStepTarget(null)}
        onConfirm={() => deleteStepMutation.mutate(deleteStepTarget.id)}
        title="Remove step"
        message={`Remove "${deleteStepTarget?.title}" from this workflow? Candidates already past this step keep their history.`}
        confirmLabel="Remove"
        loading={deleteStepMutation.isPending}
      />

      <ConfirmDialog
        open={!!deleteGroupTarget}
        onClose={() => setDeleteGroupTarget(null)}
        onConfirm={() => deleteGroupMutation.mutate(deleteGroupTarget.id)}
        title="Remove group"
        message={`Remove the "${deleteGroupTarget?.name}" group? Its steps become ungrouped, not deleted.`}
        confirmLabel="Remove"
        loading={deleteGroupMutation.isPending}
      />

      <ConfirmDialog
        open={bulkDeleteOpen}
        onClose={() => setBulkDeleteOpen(false)}
        onConfirm={() => bulkDeleteMutation.mutate(selectedStepIds)}
        title="Remove selected steps"
        message={`Remove ${selectedStepIds.length} step${selectedStepIds.length === 1 ? '' : 's'} from this workflow? Candidate Registration is fixed and isn't included. Candidates already past these steps keep their history.`}
        confirmLabel="Remove"
        loading={bulkDeleteMutation.isPending}
      />
    </div>
  );
}
